/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The attendances board on a phone: one column at a time.
 *
 * Five columns side by side need about 1400px. The three ways to fit them on
 * 400px are a horizontal scroller, an accordion, or this — pick the column
 * with a pill and give it the whole width. The pills are what the desktop
 * column headers were: they carry the stage colour and its count, so the
 * shape of the funnel is still readable without any column being on screen.
 *
 * This mirrors `VisitsKanbanBoard.mobile`'s tabs layout on purpose. Two
 * boards that work differently on the same phone is a worse cost than the
 * duplication, and Visitas got there first.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { ChevronLeftIcon, ChevronRightIcon } from '@/shared/ui/radix-icons';
import { useSlidingStrip } from '@/shared/hooks/use-sliding-strip';
import { dealAccentForStage } from '@features/deals/utils/deal-state';
import type { DealStage } from '@features/deals/constants/deal-stages';
import { AttendanceCard } from './AttendanceCard';
import type { Attendance } from '../hooks/useAttendances';
import type { AttendanceColumnId } from '../constants/attendance-columns';

/** The three ways this board reads on a phone. Mirrors Visitas. */
export type MobileBoardLayout = 'tabs' | 'stack' | 'carousel';

export interface MobileBoardColumn {
  id: AttendanceColumnId;
  label: string;
  /** Server-side total, which can exceed the page this view holds. */
  count: number;
  items: Attendance[];
  /** First stage of the column, used for its accent. */
  stage: DealStage;
}

interface AttendancesMobileBoardProps {
  agencyId: string;
  columns: MobileBoardColumn[];
  /**
   * How the stages are laid out, matching what Visitas offers on a phone.
   *
   * `tabs` picks one stage with a pill. `stack` drops the stages entirely
   * and lists every attendance — the page's "table" means this here, since
   * a six-column table is unreadable at 400px. `carousel` keeps the columns
   * as columns and swipes between them, which is the only one of the three
   * that shows a stage has a neighbour.
   */
  layout?: MobileBoardLayout;
  /** Opens the move sheet for one attendance. */
  onMoveStage: (attendance: Attendance) => void;
}

export const AttendancesMobileBoard: React.FC<AttendancesMobileBoardProps> = ({
  agencyId, columns, layout = 'tabs', onMoveStage,
}) => {
  const { t } = useTranslation('features/attendances');
  const [activeId, setActiveId] = useState<AttendanceColumnId | null>(null);
  const strip = useSlidingStrip();

  /*
   * The first column that has anything in it, not simply the first column.
   * Landing on an empty "Novos" every morning makes the board look broken
   * when the work is two columns over.
   */
  const active = useMemo(
    () => columns.find((column) => column.id === activeId)
      ?? columns.find((column) => column.items.length > 0)
      ?? columns[0],
    [columns, activeId],
  );

  const everything = useMemo(
    () => columns.flatMap((column) => column.items),
    [columns],
  );

  if (!active) return null;

  return (
    <div data-testid="attendances-mobile-board" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {layout === 'tabs' && (
        <>
          {/*
            One line that slides, not four that wrap.
            
            Wrapping was fine for the prototype's five stages. This agency
            has seven, and at 400px they became four rows -- 168px, a quarter
            of the screen, pushing the first card to y=539 so barely one was
            visible. A strip costs one row whatever the count, and the fade
            says there are more behind the edge.
          */}
          <div
            ref={strip.ref}
            {...strip.props}
            role="tablist"
            aria-label={t('board.stages')}
            className={strip.props.className}
            style={{ ...strip.props.style, display: 'flex', gap: 'var(--space-2)' }}
          >
            {columns.map((column) => (
              <StagePill
                key={column.id}
                column={column}
                active={column.id === active.id}
                onSelect={() => setActiveId(column.id)}
                wasDragged={strip.wasDragged}
              />
            ))}
          </div>

          <ColumnPanel
            column={active}
            agencyId={agencyId}
            onMoveStage={onMoveStage}
            emptyLabel={t('board.emptyColumn')}
          />
        </>
      )}

      {layout === 'carousel' && (
        <CarouselLayout
          columns={columns}
          agencyId={agencyId}
          onMoveStage={onMoveStage}
          emptyLabel={t('board.emptyColumn')}
          previousLabel={t('board.previousStage')}
          nextLabel={t('board.nextStage')}
        />
      )}

      {layout === 'stack' && (
        <div data-testid="attendances-mobile-list" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {everything.length === 0 ? (
            <EmptyLine label={t('board.emptyColumn')} />
          ) : (
            everything.map((attendance) => (
              <AttendanceCard
                key={attendance.dealId}
                agencyId={agencyId}
                attendance={attendance}
                onMoveStage={() => onMoveStage(attendance)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Full-width columns that snap, with chevrons and dots saying so.
 *
 * The pills answer "show me this stage"; the carousel answers "what is next
 * to it". Swiping alone is invisible, so the header names the stage you are
 * on and the dots say how many there are — on a board of seven stages, a
 * silent swipe leaves you guessing whether you reached the end.
 */
const CarouselLayout: React.FC<{
  columns: MobileBoardColumn[];
  agencyId: string;
  onMoveStage: (attendance: Attendance) => void;
  emptyLabel: string;
  previousLabel: string;
  nextLabel: string;
}> = ({ columns, agencyId, onMoveStage, emptyLabel, previousLabel, nextLabel }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);

  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(columns.length - 1, index));
    const child = track.children[clamped] as HTMLElement | undefined;
    if (child) track.scrollTo({ left: child.offsetLeft - track.offsetLeft, behavior: 'smooth' });
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    /* Derived from the scroll rather than set on click: the finger can move
       the track too, and a page number only the buttons update would
       disagree with what is on screen the first time somebody swipes. */
    const onScroll = () => {
      const index = Math.round(track.scrollLeft / track.clientWidth);
      setPage(Math.max(0, Math.min(columns.length - 1, index)));
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    return () => track.removeEventListener('scroll', onScroll);
  }, [columns.length]);

  const current = columns[Math.min(page, columns.length - 1)];
  if (!current) return null;
  const accent = `var(--accent-${dealAccentForStage(current.stage)})`;

  return (
    <div data-testid="attendances-mobile-carousel" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
        <PagerButton label={previousLabel} onClick={() => goTo(page - 1)} disabled={page === 0}>
          <ChevronLeftIcon width={18} height={18} />
        </PagerButton>

        <Text
          component="span"
          className="min-w-0 truncate"
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
            fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-bold)',
            color: 'var(--text-primary)',
          }}
        >
          <Text component="span" aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 'var(--radius-full)', background: accent, flex: 'none' }} />
          {current.label} · {current.count}
        </Text>

        <PagerButton label={nextLabel} onClick={() => goTo(page + 1)} disabled={page === columns.length - 1}>
          <ChevronRightIcon width={18} height={18} />
        </PagerButton>
      </div>

      <div
        ref={trackRef}
        className="scrollbar-none"
        style={{
          display: 'flex', gap: 'var(--space-3)', overflowX: 'auto',
          scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch',
        }}
      >
        {columns.map((column) => (
          <div key={column.id} style={{ flex: '0 0 100%', minWidth: '100%', scrollSnapAlign: 'start' }}>
            <ColumnPanel
              column={column}
              agencyId={agencyId}
              onMoveStage={onMoveStage}
              emptyLabel={emptyLabel}
            />
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-2)', paddingTop: 'var(--space-1)' }}>
        {columns.map((column, index) => (
          <UnstyledButton
            key={column.id}
            type="button"
            aria-label={column.label}
            aria-current={index === page}
            onClick={() => goTo(index)}
            style={{
              width: index === page ? 18 : 6,
              height: 6,
              borderRadius: 'var(--radius-full)',
              background: index === page ? `var(--accent-${dealAccentForStage(column.stage)})` : 'var(--border-medium)',
              transition: 'width var(--duration-fast) var(--ease-in-out)',
            }}
          >
            {/* The dot is the whole control; the name is on the label. */}
            <Text component="span" className="sr-only">{column.label}</Text>
          </UnstyledButton>
        ))}
      </div>
    </div>
  );
};

const PagerButton: React.FC<{
  label: string;
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
}> = ({ label, onClick, disabled, children }) => (
  <UnstyledButton
    type="button"
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
    className="flex flex-none items-center justify-center rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-primary)] text-[var(--text-secondary)]"
    style={{ width: 36, height: 36, opacity: disabled ? 0.35 : 1 }}
  >
    {children}
  </UnstyledButton>
);

const StagePill: React.FC<{
  column: MobileBoardColumn;
  active: boolean;
  onSelect: () => void;
  /** Dragging the strip past a pill must not select that stage. */
  wasDragged?: () => boolean;
}> = ({ column, active, onSelect, wasDragged }) => {
  const accent = `var(--accent-${dealAccentForStage(column.stage)})`;

  return (
    <UnstyledButton
      role="tab"
      aria-selected={active}
      data-testid={`stage-pill-${column.id}`}
      onClickCapture={(e) => { if (wasDragged?.()) { e.preventDefault(); e.stopPropagation(); } }}
      onClick={onSelect}
      className="flex flex-none items-center"
      style={{
        gap: 'var(--space-2)',
        scrollSnapAlign: 'start',
        minHeight: 36,
        padding: 'var(--space-1-5) var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${active ? accent : 'var(--border-subtle)'}`,
        background: active
          ? `color-mix(in srgb, ${accent} 10%, var(--surface-primary))`
          : 'var(--surface-primary)',
        color: active ? accent : 'var(--text-secondary)',
        fontSize: 'var(--text-meta)',
        fontWeight: 'var(--font-semibold)',
        whiteSpace: 'nowrap',
      }}
    >
      <Text
        component="span"
        aria-hidden="true"
        style={{ width: 8, height: 8, borderRadius: 'var(--radius-full)', background: accent }}
      />
      {column.label}
      <Text
        component="span"
        style={{
          minWidth: 20,
          padding: '0 var(--space-1)',
          borderRadius: 'var(--radius-full)',
          background: `color-mix(in srgb, ${accent} 14%, var(--surface-primary))`,
          color: accent,
          fontSize: 'var(--text-eyebrow)',
          lineHeight: 1.6,
          textAlign: 'center',
        }}
      >
        {column.count}
      </Text>
    </UnstyledButton>
  );
};

const ColumnPanel: React.FC<{
  column: MobileBoardColumn;
  agencyId: string;
  onMoveStage: (attendance: Attendance) => void;
  emptyLabel: string;
}> = ({ column, agencyId, onMoveStage, emptyLabel }) => {
  const accent = `var(--accent-${dealAccentForStage(column.stage)})`;

  return (
    <div
      data-testid="attendances-mobile-column"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-subtle)',
        background: 'var(--surface-card)',
      }}
    >
      <Text
        component="span"
        style={{
          alignSelf: 'flex-start',
          paddingBottom: 'var(--space-1)',
          borderBottom: `2px solid ${accent}`,
          fontSize: 'var(--text-body-sm)',
          fontWeight: 'var(--font-bold)',
          color: 'var(--text-primary)',
        }}
      >
        {column.label} · {column.count}
      </Text>

      {column.items.length === 0 ? (
        <EmptyLine label={emptyLabel} />
      ) : (
        column.items.map((attendance) => (
          <AttendanceCard
            key={attendance.dealId}
            agencyId={agencyId}
            attendance={attendance}
            onMoveStage={() => onMoveStage(attendance)}
          />
        ))
      )}
    </div>
  );
};

const EmptyLine: React.FC<{ label: string }> = ({ label }) => (
  <Text
    data-testid="attendances-mobile-empty"
    style={{
      margin: 0,
      padding: 'var(--space-6) 0',
      textAlign: 'center',
      fontSize: 'var(--text-body-sm)',
      color: 'var(--text-tertiary)',
    }}
  >
    {label}
  </Text>
);


export default AttendancesMobileBoard;
